"use client";

import type { ProductCard as ProductCardData } from "@ellaandil/formatters";
import useEmblaCarousel from "embla-carousel-react";
import { useCallback, useEffect, useState } from "react";
import { ProductCard } from "@/components/ui/ProductCard";
import { Scrollbar } from "@/components/ui/Scrollbar";
import { Typography } from "@/components/ui/Typography";
import { useTranslations } from "@/i18n/client";

interface CarouselProps {
	products: ProductCardData[];
	size: "small" | "medium";
	slideClassName: string;
	emblaClassName?: string;
}

const SLIDE_GAP = 8;

const Carousel = ({ products, size, slideClassName, emblaClassName }: CarouselProps) => {
	const [selectedSnap, setSelectedSnap] = useState(0);
	const [snapCount, setSnapCount] = useState(0);

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: "start",
		containScroll: "trimSnaps",
		// Page through a whole view at a time on prev/next instead of one card.
		slidesToScroll: "auto",
		breakpoints: { "(max-width: 639px)": { align: "center" as const } },
	});

	const onSelect = useCallback(() => {
		if (!emblaApi) return;
		setSelectedSnap(emblaApi.selectedScrollSnap());
		setSnapCount(emblaApi.scrollSnapList().length);
	}, [emblaApi]);

	useEffect(() => {
		if (!emblaApi) return;
		emblaApi.on("select", onSelect);
		emblaApi.on("reInit", onSelect);
		onSelect();
		return () => {
			emblaApi.off("select", onSelect);
			emblaApi.off("reInit", onSelect);
		};
	}, [emblaApi, onSelect]);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	return (
		<>
			<div ref={emblaRef} className={`overflow-hidden ${emblaClassName ?? ""}`}>
				<div
					className="flex [&>*:last-child]:mr-4 sm:[&>*:last-child]:mr-0"
					style={{ gap: SLIDE_GAP }}
				>
					{products.map((product) => (
						<div key={product.id} className={slideClassName}>
							<ProductCard showBrand={false} product={product} size={size} />
						</div>
					))}
				</div>
			</div>

			{snapCount > 1 && (
				<Scrollbar
					current={selectedSnap + 1}
					total={snapCount}
					onPrev={scrollPrev}
					onNext={scrollNext}
				/>
			)}
		</>
	);
};

interface RelatedProductsProps {
	products: ProductCardData[];
}

export const RelatedProducts = ({ products }: RelatedProductsProps) => {
	const t = useTranslations();
	if (products.length === 0) return null;

	return (
		<div className="flex flex-col gap-4 w-full">
			<Typography variant="body-sm-reg" className="text-text-dark-text-1">
				{t("Complete the look")}
			</Typography>

			{/* Mobile/Tablet: medium cards — 1.5 visible on small, 3 on sm+ */}
			<div className="flex flex-col gap-4 lg:hidden">
				<Carousel
					products={products}
					size="medium"
					slideClassName="min-w-0 shrink-0 grow-0 basis-[calc(66.666%-4px)] sm:basis-[calc(33.333%-6px)]"
					emblaClassName="-mr-4 sm:mr-0"
				/>
			</div>

			{/* Desktop: small cards, 3 per view */}
			<div className="hidden lg:flex lg:flex-col lg:gap-4">
				<Carousel
					products={products}
					size="small"
					slideClassName="min-w-0 shrink-0 grow-0 basis-[calc(33.333%-6px)]"
				/>
			</div>
		</div>
	);
};
